import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Globe, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeEmail } from "@/lib/api";
import { useCaptcha } from "@/lib/useCaptcha";

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

const ContactSection = ({ onOpenContact, onOpenAssessment }) => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const getCaptchaToken = useCaptcha();

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    try {
      setLoading(true);
      const token = await getCaptchaToken("subscribe");
      await subscribeEmail(email, token, website);
      toast.success("You're on the list. Confirmation sent to your inbox.");
      setEmail("");
      setWebsite("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not subscribe. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-28 lg:py-36 overflow-hidden"
      style={{ background: "var(--bg-soft)" }}
      data-testid="contact-section"
    >
      <div className="absolute inset-0 radial-fade opacity-70" />
      <div className="absolute inset-0 radial-crimson opacity-80" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Notify Me */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7 }}
            >
              <span className="eyebrow">Stay in the loop</span>
              <h2
                className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-[1.05]"
                style={{ color: "var(--text)" }}
              >
                Engineering updates
                <br />
                <span className="font-medium">straight to your inbox.</span>
              </h2>
              <p
                className="mt-5 max-w-xl leading-relaxed"
                style={{ color: "var(--text-muted)" }}
              >
                Be the first to hear about new projects, technical insights and
                launch news from the TAFRA engineering team.{" "}
                <span style={{ color: "var(--accent-blue)" }}>
                  Two emails a quarter, max. Unsubscribe anytime.
                </span>
              </p>

              <form
                onSubmit={onSubscribe}
                className="mt-10 max-w-xl"
                data-testid="notify-form"
              >
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  data-testid="notify-honeypot"
                  aria-hidden="true"
                  className="absolute opacity-0 -z-10 pointer-events-none w-0 h-0"
                  name="website"
                />
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-0">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    data-testid="notify-email-input"
                    className="flex-1 btn-sharp px-5 py-4 focus:outline-none transition-colors"
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      color: "var(--text)",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "var(--accent-blue)")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "var(--border)")
                    }
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="notify-submit-btn"
                    className="group inline-flex items-center justify-center gap-2 px-7 py-4 font-semibold btn-sharp tracking-wide transition-all duration-300 disabled:opacity-60"
                    style={{
                      background: "var(--accent-blue)",
                      color: "var(--bg)",
                      boxShadow: "0 0 18px var(--accent-blue-glow)",
                    }}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Notify Me
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </div>
                <p
                  className="mt-3 text-xs"
                  style={{ color: "var(--text-faint)" }}
                >
                  Spam-protected. Light-touch communication — your address is
                  never shared.
                </p>
              </form>

              <div className="mt-12 flex flex-wrap gap-4">
                <button
                  onClick={onOpenContact}
                  data-testid="contact-section-contact-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 btn-sharp transition-all duration-300"
                  style={{
                    border: "1px solid var(--border-strong)",
                    color: "var(--text)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--accent-blue)";
                    e.currentTarget.style.color = "var(--accent-blue)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border-strong)";
                    e.currentTarget.style.color = "var(--text)";
                  }}
                >
                  <Mail className="w-4 h-4" />
                  Send a message
                </button>
                <button
                  onClick={onOpenAssessment}
                  data-testid="contact-section-assessment-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 btn-sharp transition-all duration-300"
                  style={{
                    border: "1px solid var(--accent-crimson)",
                    background: "var(--accent-crimson-soft)",
                    color: "var(--text)",
                  }}
                >
                  Request a site assessment
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>

          {/* Contact card */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative p-8 lg:p-10 btn-sharp backdrop-blur-xl"
              style={{
                background: "var(--surface-elev)",
                border: "1px solid var(--border)",
              }}
            >
              <span className="eyebrow">Direct line</span>
              <h3
                className="mt-3 font-display text-2xl font-medium"
                style={{ color: "var(--text)" }}
              >
                Talk to our engineering team
              </h3>
              <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
                We respond to project enquiries within one business day.
              </p>

              <div className="mt-8 space-y-5">
                <ContactRow
                  href="tel:+254725979245"
                  Icon={Phone}
                  label="Phone"
                  value="+254 725 979 245"
                  accent="var(--accent-blue)"
                  testid="contact-phone-link"
                />
                <ContactRow
                  href="mailto:contact@tafraenergy.com"
                  Icon={Mail}
                  label="Email"
                  value="contact@tafraenergy.com"
                  accent="var(--accent-crimson-hover)"
                  testid="contact-email-link"
                />
                <ContactRow Icon={MapPin} label="Address" value="Nairobi, KE" />
                <ContactRow Icon={Globe} label="Website" value="www.tafraenergy.com" />
              </div>

              <span
                className="absolute -top-px right-12 h-px w-24"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, var(--accent-blue), transparent)",
                }}
              />
              <span
                className="absolute -bottom-px left-12 h-px w-24"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, var(--accent-crimson), transparent)",
                }}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactRow = ({ href, Icon, label, value, accent, testid }) => {
  const content = (
    <>
      <span
        className="w-10 h-10 flex items-center justify-center btn-sharp transition-colors"
        style={{ border: "1px solid var(--border)" }}
      >
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </span>
      <span>
        <span
          className="block text-[11px] tracking-[0.25em] uppercase"
          style={{ color: "var(--text-faint)" }}
        >
          {label}
        </span>
        <span
          className="block mt-1 font-display text-lg break-all"
          style={{ color: "var(--text)" }}
        >
          {value}
        </span>
      </span>
    </>
  );
  if (!href) {
    return (
      <div className="flex items-start gap-4" style={{ color: "var(--text)" }}>
        {content}
      </div>
    );
  }
  return (
    <a
      href={href}
      data-testid={testid}
      className="group flex items-start gap-4 transition-colors"
      style={{ color: "var(--text)" }}
      onMouseEnter={(e) => (e.currentTarget.style.color = accent)}
      onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text)")}
    >
      {content}
    </a>
  );
};

export default ContactSection;
